/**
 * Shared app-shell header (FR-9.8, roadmap FR-Future-8). Rendered on every surface so
 * the "Jeopardy" title is always a working way **home** — no URL editing needed. The
 * title is a router `Link to="/"`; an optional role badge names the surface and an
 * optional {@link ConnectionBadge} shows live-connection status (it self-hides when
 * idle).
 *
 * `onHomeIntercept` is the safe-exit guard (D-F8.2): a surface with an active game
 * passes a callback that returns `false` to CANCEL the navigation (e.g. a confirm the
 * user dismissed). With no guard, home navigation is immediate. The guard is a UX
 * safeguard only — the WS reconnect model retains the seat/host session regardless
 * (FR-9.3/FR-2.6), so it protects against an accidental mid-game exit, not data loss.
 */

import { Link } from 'react-router-dom';
import { ConnectionBadge } from './ConnectionBadge';
import { BRAND_NAME } from './brand';
import type { ConnectionStatus } from '../net/use-game';

export interface AppHeaderProps {
  /** Short surface label shown as a badge (e.g. `Player`/`Host`/`Presentation`). */
  role?: string;
  /** Live-connection status → renders {@link ConnectionBadge} (nothing when idle). */
  status?: ConnectionStatus;
  /**
   * Optional guard run when the home link is clicked. Return `false` to cancel the
   * navigation; `true` (or omit the prop) to allow it. See {@link homeGuard}.
   */
  onHomeIntercept?: () => boolean;
}

export function AppHeader({
  role,
  status,
  onHomeIntercept,
}: AppHeaderProps): React.JSX.Element {
  return (
    <header className="app-header">
      <Link
        to="/"
        className="app-title app-title--link"
        data-testid="nav-home"
        onClick={(e) => {
          if (onHomeIntercept && !onHomeIntercept()) e.preventDefault();
        }}
      >
        <img src="/peril-mark.svg" className="app-logo" alt="" aria-hidden="true" />
        {BRAND_NAME}
      </Link>
      {role !== undefined && <span className="role-badge">{role}</span>}
      {status !== undefined && <ConnectionBadge status={status} />}
    </header>
  );
}
